import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEvents } from '../services/api';
import { CalendarDays, Users, CheckCircle, Clock, ChevronRight, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

function StatCard({ val, lbl, color }) {
  return (
    <div className="stat-card">
      <div className="val" style={{ color }}>{val}</div>
      <div className="lbl">{lbl}</div>
    </div>
  );
}

function rsvpPercent(e) {
  if (!e.total_invited || e.total_invited === 0) return 0;
  return Math.round(((+e.confirmed + +e.declined) / +e.total_invited) * 100);
}

export default function DashboardPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getEvents()
      .then(r => setEvents(r.data))
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, []);

  const totalInvited   = events.reduce((s, e) => s + (+e.total_invited || 0), 0);
  const totalConfirmed = events.reduce((s, e) => s + (+e.confirmed || 0), 0);
  const totalPending   = events.reduce((s, e) => s + (+e.pending || 0), 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Tableau de bord</h1>
          <p className="text-muted">{events.length} événement{events.length !== 1 ? 's' : ''} au total</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/events')}>
          <Plus size={15} /> Nouvel événement
        </button>
      </div>

      <div className="stats-grid">
        <StatCard val={events.length} lbl="Événements" color="var(--accent)" />
        <StatCard val={totalInvited}  lbl="Invités total" color="var(--text)" />
        <StatCard val={totalConfirmed} lbl="Confirmés" color="var(--green)" />
        <StatCard val={totalPending}  lbl="En attente" color="var(--amber)" />
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2>Événements récents</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/events')}>Voir tout</button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <CalendarDays />
            <h3>Aucun événement</h3>
            <p>Créez votre premier événement pour commencer.</p>
            <button className="btn btn-primary mt-4" onClick={() => navigate('/events')}>
              <Plus size={15} /> Créer un événement
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {events.slice(0, 5).map(ev => (
              <div
                key={ev.id}
                onClick={() => navigate(`/events/${ev.id}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '14px 16px', background: 'var(--bg3)',
                  borderRadius: 'var(--radius)', cursor: 'pointer',
                  border: '1px solid var(--border)', transition: 'border-color 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: 10,
                  background: 'linear-gradient(135deg, #4f7cff22, #7c5cfc22)',
                  border: '1px solid #4f7cff33',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <CalendarDays size={18} color="#4f7cff" />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{ev.name}</div>
                  <div className="text-muted text-sm">
                    {ev.event_date ? new Date(ev.event_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Date non définie'}
                    {ev.location ? ` · ${ev.location}` : ''}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{ev.total_invited || 0}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>invités</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--green)' }}>{ev.confirmed || 0}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>confirmés</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--amber)' }}>{ev.pending || 0}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>en attente</div>
                  </div>
                </div>

                <div style={{ marginLeft: 8 }}>
                  <div className="progress-bar" style={{ width: 80, marginBottom: 4 }}>
                    <div className="progress-fill" style={{ width: `${rsvpPercent(ev)}%` }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right' }}>{rsvpPercent(ev)}% répondu</div>
                </div>

                <ChevronRight size={16} color="var(--text3)" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
